/**
 * canvas-engine/presets/presetData/flexOfficePresets.js
 *
 * TRANSCRIBED FlexOffice (workspace booking) default look-and-feel VALUES.
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1): imports NOTHING. Values transcribed from
 * the Default_Source (`flexOfficeTemplateDefaults.js` + `ConsoleFlexOfficePreview`)
 * as plain DATA. No React, no rendering, no side effects.
 *
 * See educationPresets.js for the shared descriptor shape. Consumed by the own
 * decomposition adapters (Task 3.2/3.3).
 *
 * Requirements: 3.6, 3.7, 5.1, 5.4, 5.6, 5.7
 */

/** Default imagery (transcribed from `flexOfficeImages`). */
export const flexOfficeImages = {
  HERO_ONE: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=85&fit=crop&auto=format',
  HERO_TWO: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&q=85&fit=crop&auto=format',
  HERO_THREE: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&fit=crop&auto=format',
  ABOUT: 'https://cdn.rareblocks.xyz/collection/celebration/images/content/3/girl-working-on-laptop.jpg',
  FEATURE_FLEXIBLE: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=75&fit=crop&auto=format',
  FEATURE_PREMIUM: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=600&q=75&fit=crop&auto=format',
  FEATURE_WIFI: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=75&fit=crop&auto=format',
  FEATURE_MEETING: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=600&q=75&fit=crop&auto=format',
  SERVICE_ROOM: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=900&q=80&fit=crop&auto=format',
  SERVICE_DESK: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=900&q=80&fit=crop&auto=format',
  SERVICE_LOUNGE: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=900&q=80&fit=crop&auto=format',
  CONTACT: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Flex+Office+Domain+Assets/contact.png',
};

/** Theme-level style values transcribed from FlexOffice `themeDefaults`. */
export const flexOfficeTheme = {
  primary: '#23196E',
  secondary: '#0f172a',
  accent: '#23196E',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#1e293b',
  mutedColor: '#64748b',
  borderColor: '#e2e8f0',
  baseFontSize: '16px',
  headingWeight: '800',
  lineHeight: '1.6',
  pageWidth: '1280px',
  sectionSpacing: '80px 24px',
  gridGap: '28px',
  buttonRadius: '12px',
  cardRadius: '20px',
  cardShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
  imageRadius: '20px',
  headerHeight: '64px',
  footerBackground: '#0f172a',
};

// ── Local pure data helpers ──
const img = (url = '') => ({ storedLink: '', displayLink: url });
const action = (label, url, variant = 'primary') => ({ label, url, variant, visible: true });
const block = (title, description = '', icon = '', extra = {}) => ({
  title,
  description,
  icon,
  visible: true,
  ...extra,
});
const style = (overrides = {}) => ({
  background: '#ffffff',
  textColor: '#1e293b',
  accentColor: '#23196E',
  secondaryColor: '#0f172a',
  padding: '80px 24px',
  ...overrides,
});

/**
 * FLEX_OFFICE_PRESETS — per-section-type default descriptors.
 * Covers hero, about, timeline, workingHours, features, cta, servicesPage,
 * serviceDetail, bookingSearch, bookings, invoice, contact.
 */
export const FLEX_OFFICE_PRESETS = {
  flexOfficeHero: {
    sectionType: 'flexOfficeHero',
    label: 'Flex hero',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', textColor: '#ffffff', padding: '0', minHeight: '70vh' }),
    layout: { align: 'center', width: 'full', columns: 1 },
    content: {
      badge: 'Premium Workspaces, When You Need Them',
      heading: 'Flexible Workspaces for Modern Professionals',
      headingParts: { prefix: 'Flexible Workspaces for', accent: 'Modern Professionals' },
      subheading:
        'Book fully equipped conference rooms and workstations on your terms-hourly, daily, or weekly. Choose your plan. No commitments. Just productivity.',
      images: [img(flexOfficeImages.HERO_ONE), img(flexOfficeImages.HERO_TWO), img(flexOfficeImages.HERO_THREE)],
      actions: [
        action('Book Your Workspace', '/flex-home', 'primary'),
        action('Contact Us', '/contact', 'outline'),
      ],
    },
  },

  flexOfficeAbout: {
    sectionType: 'flexOfficeAbout',
    label: 'About workspace',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '104px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'About Us',
      heading: 'Designed for the Way You Work Today',
      headingParts: { prefix: 'Designed for the Way', accent: 'You Work Today' },
      body:
        'Premium coworking, private offices, conference rooms, and flexible booking options designed for modern teams and independent professionals.',
      quote:
        'Great teams are built on seamless collaboration, and FlexOffice is the tool that makes it happen.',
      images: [img(flexOfficeImages.ABOUT)],
      imageAlt: 'Professional workspace',
      actions: [action('Learn More About CoworkFlex', '/our-features', 'primary')],
    },
  },

  flexOfficeTimeline: {
    sectionType: 'flexOfficeTimeline',
    label: 'Booking steps',
    domain: 'flexOffice',
    style: style({ background: '#0f172a', textColor: '#ffffff', padding: '104px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'How It Works',
      heading: 'Book Your Workspace in 4 Simple Steps',
      headingParts: { prefix: 'Book Your Workspace in', accent: '4 Simple Steps' },
      actions: [action('Start Booking Now', '/flex-home', 'primary')],
      blocks: [
        block('Choose Your Space', 'Select a conference room or workstation that fits your needs.', 'Building', { gradient: 'from-stone-400 to-stone-600' }),
        block('Pick Date & Duration', 'Book by the hour, day, or week-total flexibility.', 'Calendar', { gradient: 'from-emerald-400 to-emerald-600' }),
        block('Set Capacity', 'Add the number of attendees to ensure you have the perfect setup.', 'Users', { gradient: 'from-stone-300 to-stone-500' }),
        block('Confirm & Go', 'Review your details and confirm your booking instantly.', 'Check', { gradient: 'from-amber-400 to-amber-600' }),
      ],
    },
  },

  flexOfficeWorkingHours: {
    sectionType: 'flexOfficeWorkingHours',
    label: 'Business hours',
    domain: 'flexOffice',
    style: style({ background: '#eef4ff', padding: '88px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 1 },
    content: {
      eyebrow: 'Business Hours',
      heading: "We're Here For You",
      headingParts: { prefix: "We're Here For", accent: 'You' },
      subheading: 'Visit us during business hours or book ahead for guaranteed availability.',
      openLabel: 'Opens',
      openTime: '09:00 AM',
      closeLabel: 'Closes',
      closeTime: '06:00 PM',
      scheduleTitle: 'Weekly Schedule',
      workingDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      infoText:
        'Planning a visit? Morning hours typically have shorter wait times and better availability.',
      actions: [action('Contact Support', '/contact', 'primary')],
    },
  },

  flexOfficeFeatures: {
    sectionType: 'flexOfficeFeatures',
    label: 'Feature cards',
    domain: 'flexOffice',
    style: style({ background: '#0f172a', textColor: '#ffffff', padding: '104px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'Why Choose CoworkFlex',
      heading: 'Simple. Flexible. Built for How You Work.',
      headingParts: { prefix: 'Simple. Flexible.', accent: 'Built for How You Work.' },
      subheading:
        'Everything you need to work, meet, and collaborate-without the overhead of a traditional office.',
      cards: [
        block('Flexible Terms', 'Choose pay-as-you-go access-hourly, daily, weekly, or monthly.', 'Zap', { imageContent: img(flexOfficeImages.FEATURE_FLEXIBLE), gradient: 'from-stone-400 to-stone-600' }),
        block('Premium Setup', 'Modern, fully furnished spaces designed for productivity.', 'Sparkles', { imageContent: img(flexOfficeImages.FEATURE_PREMIUM), gradient: 'from-emerald-400 to-emerald-600' }),
        block('Fast Internet', 'Fast, dependable Wi-Fi for calls, collaboration, and focused work.', 'Wifi', { imageContent: img(flexOfficeImages.FEATURE_WIFI), gradient: 'from-amber-400 to-amber-600' }),
        block('Meeting Rooms', 'Fully equipped, professional meeting rooms-book when you need them.', 'Shield', { imageContent: img(flexOfficeImages.FEATURE_MEETING), gradient: 'from-rose-400 to-rose-600' }),
      ],
    },
  },

  flexOfficeCta: {
    sectionType: 'flexOfficeCta',
    label: 'Signup CTA',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '76px 24px' }),
    layout: { align: 'center', width: 'contained' },
    content: {
      eyebrow: 'Join Our Community',
      heading: 'Ready to Book Your Next Workspace?',
      headingParts: { prefix: 'Ready to Book Your', accent: 'Next Workspace?' },
      subheading:
        'Discover flexible, professional workspaces designed to help you work better-without long-term commitments.',
      actions: [
        action('Sign Up Now', '/sign-up', 'primary'),
        action('Already a Member? Sign In', '/sign-in', 'outline'),
      ],
    },
  },

  flexOfficeServicesPage: {
    sectionType: 'flexOfficeServicesPage',
    label: 'Services listing',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '104px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'All Services',
      subheading: 'Browse conference rooms, workstations, private offices, and collaboration spaces.',
      searchPlaceholder: 'Search products...',
      categoryLabel: 'All Categories',
      cards: [
        block('Executive Conference Room', 'Boardroom setup with display, whiteboard, and premium seating.', 'Meeting', { imageContent: img(flexOfficeImages.SERVICE_ROOM), price: '$45', period: 'per hour', badge: 'Meeting Room', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Dedicated Workstation', 'Quiet desk with fast internet, power, and ergonomic setup.', 'Desk', { imageContent: img(flexOfficeImages.SERVICE_DESK), price: '$18', period: 'per hour', badge: 'Workspace', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Team Collaboration Lounge', 'Open lounge for workshops, brainstorming, and client sessions.', 'Users', { imageContent: img(flexOfficeImages.SERVICE_LOUNGE), price: '$75', period: 'per hour', badge: 'Team Space', buttonLabel: 'View Details', url: '/selected-product-details' }),
      ],
      // Live catalog renders inside this chrome.
      binding: { source: 'services' },
    },
  },

  flexOfficeServiceDetail: {
    sectionType: 'flexOfficeServiceDetail',
    label: 'Service detail',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Workspace Details',
      heading: 'Executive Conference Room',
      subheading:
        'A polished room for client meetings, board sessions, interviews, and collaborative workshops.',
      images: [img(flexOfficeImages.SERVICE_ROOM)],
      priceLabel: '$45',
      periodLabel: 'per hour',
      actions: [action('Continue Booking', '/review-schedule', 'primary')],
      blocks: [
        block('Capacity', 'Seats up to 12 people with presentation setup.', 'Users'),
        block('Amenities', 'Display, whiteboard, video call support, and high speed Wi-Fi.', 'Sparkles'),
        block('Flexible terms', 'Book hourly, daily, weekly, or recurring blocks.', 'Calendar'),
      ],
      binding: { source: 'serviceDetail' },
    },
  },

  flexOfficeBookingSearch: {
    sectionType: 'flexOfficeBookingSearch',
    label: 'Booking search',
    domain: 'flexOffice',
    style: style({ background: '#f2f0ff', padding: '80px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'Book Space',
      heading: 'Find the right workspace',
      subheading:
        'Search by location, date, duration, and space type. Live availability stays connected to the booking flow.',
      locationLabel: 'Location',
      dateLabel: 'Date',
      durationLabel: 'Duration',
      typeLabel: 'Space Type',
      actions: [action('Search Spaces', '/flex-home', 'primary')],
      blocks: [
        block('Downtown', 'Main location', 'MapPin'),
        block('Today', 'Available slots', 'Calendar'),
        block('2 hours', 'Flexible duration', 'Clock'),
        block('Meeting room', 'Workspace type', 'Building'),
      ],
      binding: { source: 'bookings' },
    },
  },

  flexOfficeBookings: {
    sectionType: 'flexOfficeBookings',
    label: 'My bookings',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'My Bookings',
      subheading: 'Review upcoming and past reservations from your workspace account.',
      emptyStateTitle: 'Bookings will appear here',
      cards: [
        block('Executive Conference Room', 'Tomorrow, 10:00 AM - 12:00 PM', 'Calendar', { badge: 'Confirmed', price: '$90' }),
        block('Dedicated Workstation', 'Friday, 09:00 AM - 05:00 PM', 'Desk', { badge: 'Upcoming', price: '$72' }),
      ],
      binding: { source: 'bookings' },
    },
  },

  flexOfficeInvoice: {
    sectionType: 'flexOfficeInvoice',
    label: 'Invoice summary',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Invoices',
      subheading: 'Track invoice status, payment references, and receipts for workspace bookings.',
      totalLabel: 'Total Due',
      statusLabel: 'Payment Status',
      actions: [action('View Invoice', '/my-invoice', 'primary')],
      cards: [
        block('INV-1008', 'Executive Conference Room', 'Receipt', { badge: 'Due', price: '$90' }),
        block('INV-1007', 'Dedicated Workstation', 'Receipt', { badge: 'Paid', price: '$72' }),
      ],
      binding: { source: 'invoices' },
    },
  },

  flexOfficeContact: {
    sectionType: 'flexOfficeContact',
    label: 'Contact support',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '104px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Contact Us',
      heading: 'Need help choosing the right workspace?',
      subheading:
        'Talk to our workspace team about availability, recurring bookings, invoices, or custom team requirements.',
      phoneTitle: 'Phone',
      phoneValue: '+1 (555) 248-1010',
      emailTitle: 'Email',
      emailValue: 'hello@flexoffice.com',
      addressTitle: 'Address',
      addressValue: '123 Workspace Ave, Suite 400\nNew York, NY 10001',
      formTitle: 'Send a Message',
      submitLabel: 'Submit Message',
      fields: [
        { label: 'First Name', type: 'text', visible: true },
        { label: 'Last Name', type: 'text', visible: true },
        { label: 'Email *', type: 'email', required: true, visible: true },
        { label: 'Message *', type: 'textarea', required: true, visible: true },
      ],
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // Flow-page Canvas_Shells (Task 3.1). Transcribed from the Default_Source
  // page→component mapping in `createFlexOfficeDefaultPages` and the rendered
  // look in `ConsoleFlexOfficePreview` (`legacyFlexSections.*`). Each carries a
  // `content.binding.source` for its live region; chrome stays editable data.
  // ───────────────────────────────────────────────────────────────────────

  // bookSpace ← legacyPageSection('SelectSlot') → FlexBookingSearch (booking()).
  flexOfficeBookSpace: {
    sectionType: 'flexOfficeBookSpace',
    label: 'Book a space',
    domain: 'flexOffice',
    style: style({ background: '#f2f0ff', padding: '80px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'Book Space',
      heading: 'Find the right workspace',
      subheading:
        'Search by location, date, duration, and space type. Live availability stays connected to the booking flow.',
      locationLabel: 'Location',
      dateLabel: 'Date',
      durationLabel: 'Duration',
      typeLabel: 'Space Type',
      actions: [action('Search Spaces', '/flex-home', 'primary')],
      blocks: [
        block('Downtown', 'Main location', 'MapPin'),
        block('Today', 'Available slots', 'Calendar'),
        block('2 hours', 'Flexible duration', 'Clock'),
        block('Meeting room', 'Workspace type', 'Building'),
      ],
      binding: { source: 'bookings' },
    },
  },

  // spaceTypes ← legacyPageSection('ViewAllCategories') → FlexServicesPage (spaceTypes()).
  flexOfficeSpaceTypes: {
    sectionType: 'flexOfficeSpaceTypes',
    label: 'Space types',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'Type of Spaces',
      subheading: 'Preview the legacy space-type browsing page.',
      searchPlaceholder: 'Search space types...',
      categoryLabel: 'All Space Types',
      cards: [
        block('Executive Conference Room', 'Boardroom setup with display, whiteboard, and premium seating.', 'Meeting', { imageContent: img(flexOfficeImages.SERVICE_ROOM), price: '$45', period: 'per hour', badge: 'Meeting Room', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Dedicated Workstation', 'Quiet desk with fast internet, power, and ergonomic setup.', 'Desk', { imageContent: img(flexOfficeImages.SERVICE_DESK), price: '$18', period: 'per hour', badge: 'Workspace', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Team Collaboration Lounge', 'Open lounge for workshops, brainstorming, and client sessions.', 'Users', { imageContent: img(flexOfficeImages.SERVICE_LOUNGE), price: '$75', period: 'per hour', badge: 'Team Space', buttonLabel: 'View Details', url: '/selected-product-details' }),
      ],
      binding: { source: 'spaceTypes' },
    },
  },

  // exploreLocations ← legacyPageSection('ExploreLocation') → FlexServicesPage (services()).
  flexOfficeExploreLocations: {
    sectionType: 'flexOfficeExploreLocations',
    label: 'Explore locations',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'Our Services',
      subheading: 'Browse available rooms, desks, and team spaces.',
      searchPlaceholder: 'Search workspaces...',
      categoryLabel: 'All Categories',
      cards: [
        block('Executive Conference Room', 'Boardroom setup with display, whiteboard, and premium seating.', 'Meeting', { imageContent: img(flexOfficeImages.SERVICE_ROOM), price: '$45', period: 'per hour', badge: 'Meeting Room', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Dedicated Workstation', 'Quiet desk with fast internet, power, and ergonomic setup.', 'Desk', { imageContent: img(flexOfficeImages.SERVICE_DESK), price: '$18', period: 'per hour', badge: 'Workspace', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Team Collaboration Lounge', 'Open lounge for workshops, brainstorming, and client sessions.', 'Users', { imageContent: img(flexOfficeImages.SERVICE_LOUNGE), price: '$75', period: 'per hour', badge: 'Team Space', buttonLabel: 'View Details', url: '/selected-product-details' }),
      ],
      binding: { source: 'locations' },
    },
  },

  // bookSchedule ← bookingSearchSection variant (createFlexOfficeDefaultPages).
  flexOfficeBookSchedule: {
    sectionType: 'flexOfficeBookSchedule',
    label: 'Book schedule',
    domain: 'flexOffice',
    style: style({ background: '#f2f0ff', padding: '80px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'Book Schedule',
      heading: 'Choose a time that works',
      subheading:
        'Review available dates, durations, and workspace types before confirming your reservation.',
      locationLabel: 'Location',
      dateLabel: 'Date',
      durationLabel: 'Duration',
      typeLabel: 'Space Type',
      actions: [action('Review Schedule', '/review-schedule', 'primary')],
      blocks: [
        block('Downtown', 'Main location', 'MapPin'),
        block('Today', 'Available slots', 'Calendar'),
        block('2 hours', 'Flexible duration', 'Clock'),
        block('Meeting room', 'Workspace type', 'Building'),
      ],
      binding: { source: 'bookings' },
    },
  },

  // choice ← servicesPageSection variant (createFlexOfficeDefaultPages).
  flexOfficeChoice: {
    sectionType: 'flexOfficeChoice',
    label: 'Choose space',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '104px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'Choose Your Space',
      subheading:
        'Compare meeting rooms, workstations, and collaboration lounges before continuing to booking.',
      searchPlaceholder: 'Search products...',
      categoryLabel: 'All Categories',
      cards: [
        block('Executive Conference Room', 'Boardroom setup with display, whiteboard, and premium seating.', 'Meeting', { imageContent: img(flexOfficeImages.SERVICE_ROOM), price: '$45', period: 'per hour', badge: 'Meeting Room', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Dedicated Workstation', 'Quiet desk with fast internet, power, and ergonomic setup.', 'Desk', { imageContent: img(flexOfficeImages.SERVICE_DESK), price: '$18', period: 'per hour', badge: 'Workspace', buttonLabel: 'View Details', url: '/selected-product-details' }),
        block('Team Collaboration Lounge', 'Open lounge for workshops, brainstorming, and client sessions.', 'Users', { imageContent: img(flexOfficeImages.SERVICE_LOUNGE), price: '$75', period: 'per hour', badge: 'Team Space', buttonLabel: 'View Details', url: '/selected-product-details' }),
      ],
      binding: { source: 'services' },
    },
  },

  // viewService ← legacyPageSection('SelectSchedule') → FlexServiceDetail (schedule('View Service')).
  flexOfficeViewService: {
    sectionType: 'flexOfficeViewService',
    label: 'View service',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Booking Flow',
      heading: 'View Service',
      subheading:
        'Static preview of the legacy schedule and reservation flow. Live date, recurrence, and payment logic stays in academy-ui.',
      images: [img(flexOfficeImages.SERVICE_ROOM)],
      priceLabel: 'Selected time',
      actions: [action('Continue', '/confirm-book', 'primary')],
      blocks: [
        block('Date', 'Choose booking date', 'Calendar'),
        block('Time Slot', 'Select available time', 'Clock'),
        block('Workspace', 'Confirm room or desk', 'Building'),
        block('Payment', 'Complete reservation', 'CreditCard'),
      ],
      binding: { source: 'serviceDetail' },
    },
  },

  // marketingFeatures ← legacyPageSection('FlexFeatures') → FlexFeatures (features()).
  flexOfficeMarketingFeatures: {
    sectionType: 'flexOfficeMarketingFeatures',
    label: 'Marketing features',
    domain: 'flexOffice',
    style: style({ background: '#0f172a', textColor: '#ffffff', padding: '96px 24px' }),
    layout: { align: 'center', width: 'contained', columns: 4 },
    content: {
      eyebrow: 'Why Choose Us',
      heading: 'Everything your workday needs',
      headingParts: { prefix: 'Everything your workday', accent: 'needs' },
      subheading: 'The same premium workspace story shown in the old Flex Office pages.',
      cards: [
        block('Flexible Booking', 'Hourly, daily, and recurring workspace access.', 'Calendar', { imageContent: img(flexOfficeImages.SERVICE_ROOM) }),
        block('Premium Amenities', 'Reliable Wi-Fi, displays, seating, and support.', 'Wifi', { imageContent: img(flexOfficeImages.SERVICE_DESK) }),
        block('Team Ready', 'Rooms and lounges for collaboration.', 'Users', { imageContent: img(flexOfficeImages.SERVICE_ROOM) }),
        block('Simple Payments', 'Invoices and reservations in one flow.', 'Receipt', { imageContent: img(flexOfficeImages.SERVICE_DESK) }),
      ],
    },
  },

  // confirmBook ← legacyPageSection('ConfirmBook') → FlexServiceDetail (schedule('Confirm Booking')).
  flexOfficeConfirmBook: {
    sectionType: 'flexOfficeConfirmBook',
    label: 'Confirm booking',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Booking Flow',
      heading: 'Confirm Booking',
      subheading:
        'Static preview of the legacy schedule and reservation flow. Live date, recurrence, and payment logic stays in academy-ui.',
      images: [img(flexOfficeImages.SERVICE_ROOM)],
      priceLabel: 'Selected time',
      actions: [action('Continue', '/confirm-book', 'primary')],
      blocks: [
        block('Date', 'Choose booking date', 'Calendar'),
        block('Time Slot', 'Select available time', 'Clock'),
        block('Workspace', 'Confirm room or desk', 'Building'),
        block('Payment', 'Complete reservation', 'CreditCard'),
      ],
      binding: { source: 'bookings' },
    },
  },

  // reviewSchedule ← legacyPageSection('ReviewSchedule') → FlexServiceDetail (schedule('Review Schedule')).
  flexOfficeReviewSchedule: {
    sectionType: 'flexOfficeReviewSchedule',
    label: 'Review schedule',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'Booking Flow',
      heading: 'Review Schedule',
      subheading:
        'Static preview of the legacy schedule and reservation flow. Live date, recurrence, and payment logic stays in academy-ui.',
      images: [img(flexOfficeImages.SERVICE_ROOM)],
      priceLabel: 'Selected time',
      actions: [action('Continue', '/confirm-book', 'primary')],
      blocks: [
        block('Date', 'Choose booking date', 'Calendar'),
        block('Time Slot', 'Select available time', 'Clock'),
        block('Workspace', 'Confirm room or desk', 'Building'),
        block('Payment', 'Complete reservation', 'CreditCard'),
      ],
      binding: { source: 'bookings' },
    },
  },

  // orders ← legacyPageSection('ViewMyOrder') → FlexBookings (bookings()).
  flexOfficeOrders: {
    sectionType: 'flexOfficeOrders',
    label: 'My orders',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'My Bookings',
      subheading: 'Review upcoming and past reservations from your workspace account.',
      emptyStateTitle: 'Bookings will appear here',
      cards: [
        block('Executive Conference Room', 'Tomorrow, 10:00 AM - 12:00 PM', 'Calendar', { badge: 'Confirmed', price: '$90' }),
        block('Dedicated Workstation', 'Friday, 09:00 AM - 05:00 PM', 'Desk', { badge: 'Upcoming', price: '$72' }),
      ],
      binding: { source: 'orders' },
    },
  },

  // customerOrders ← legacyPageSection('ViewOrderDetails') → FlexBookings (customerOrders()).
  flexOfficeCustomerOrders: {
    sectionType: 'flexOfficeCustomerOrders',
    label: 'Customer orders',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'Customer Orders',
      subheading: 'Legacy customer order cards rendered as static preview.',
      cards: [
        block('Customer Reservation', 'Room reservation awaiting payment review.', 'Calendar', { badge: 'Pending', price: '$135.00', location: 'Downtown Campus' }),
        block('Recurring Booking', 'Weekly workspace booking for a team.', 'Users', { badge: 'Recurring', price: '$420.00', location: 'North Hub' }),
        block('Add-on Order', 'Catering and AV add-ons attached to a booking.', 'Receipt', { badge: 'Add-on', price: '$86.00', location: 'Innovation Floor' }),
      ],
      binding: { source: 'orders' },
    },
  },

  // userCustomerOrders ← legacyPageSection('ViewMyOrder') → FlexBookings (bookings()).
  flexOfficeUserCustomerOrders: {
    sectionType: 'flexOfficeUserCustomerOrders',
    label: 'Customer orders (profile)',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 3 },
    content: {
      heading: 'My Bookings',
      subheading: 'Review upcoming and past reservations from your workspace account.',
      emptyStateTitle: 'Bookings will appear here',
      cards: [
        block('Executive Conference Room', 'Tomorrow, 10:00 AM - 12:00 PM', 'Calendar', { badge: 'Confirmed', price: '$90' }),
        block('Dedicated Workstation', 'Friday, 09:00 AM - 05:00 PM', 'Desk', { badge: 'Upcoming', price: '$72' }),
      ],
      binding: { source: 'orders' },
    },
  },

  // invoiceDetail ← legacyPageSection('ViewMyInvoice') → FlexInvoice (invoice()).
  flexOfficeInvoiceDetail: {
    sectionType: 'flexOfficeInvoiceDetail',
    label: 'Invoice detail',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '96px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Invoices',
      subheading: 'Track invoice status, payment references, and receipts for workspace bookings.',
      totalLabel: 'Total Due',
      statusLabel: 'Payment Status',
      actions: [action('View Invoice', '/my-invoice', 'primary')],
      cards: [
        block('INV-1008', 'Executive Conference Room', 'Receipt', { badge: 'Due', price: '$90' }),
        block('INV-1007', 'Dedicated Workstation', 'Receipt', { badge: 'Paid', price: '$72' }),
      ],
      binding: { source: 'invoices' },
    },
  },

  // aboutFlexOffice ← legacyPageSection('FlexOfficeAbout') → FlexAbout (about()).
  flexOfficeAboutPage: {
    sectionType: 'flexOfficeAboutPage',
    label: 'About FlexOffice',
    domain: 'flexOffice',
    style: style({ background: '#ffffff', padding: '92px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: 'About Flex Office',
      heading: 'Workspaces that fit your day',
      headingParts: { prefix: 'Workspaces that', accent: 'fit your day' },
      body:
        'A polished static preview of the legacy Flex Office home. Live booking and account logic stays in academy-ui.',
      quote: 'Designed for teams, founders, freelancers, and hybrid work.',
      images: [img(flexOfficeImages.ABOUT)],
      imageAlt: 'Professional workspace',
      actions: [action('View Services', '/our-services', 'primary')],
    },
  },

  // signIn/signUp ← legacyPageSection('Login')/'Register'. Auth descriptor mirrors
  // the waasAuth shape: signIn default in `content`, sign-up copy in `variants.signUp`.
  flexOfficeAuth: {
    sectionType: 'flexOfficeAuth',
    label: 'Auth page',
    domain: 'flexOffice',
    style: style({ background: '#f8fafc', padding: '78px 24px' }),
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      mode: 'signIn',
      eyebrow: 'WORKSPACE ACCOUNT',
      heading: 'Sign in to manage your workspace account.',
      subheading:
        'Sign in to manage bookings, reservations, invoices, and account details in one place.',
      formTitle: 'Sign in',
      submitLabel: 'SIGN IN',
      alternateLabel: 'Need an account? Sign up',
      alternateUrl: '/sign-up',
      images: [img(flexOfficeImages.SERVICE_ROOM)],
      imageAlt: 'FlexOffice workspace account access',
      fields: [
        { label: 'Email', type: 'email', required: true, visible: true },
        { label: 'Password', type: 'password', required: true, visible: true },
      ],
      blocks: [
        block('Manage your bookings', 'Keep upcoming reservations and history connected to your account.', '01'),
        block('Track invoices and orders', 'Use one entry point for bookings, invoices, and support follow-up.', '02'),
      ],
      // Sign-up variant (legacy Register). Preserved losslessly under
      // canvas.source.preserved.content; selected when authVariant === 'signUp'.
      variants: {
        signUp: {
          mode: 'signUp',
          eyebrow: 'CREATE YOUR ACCOUNT',
          heading: 'Create your workspace account.',
          subheading:
            'Create an account to book spaces, manage reservations, and keep invoices in one place.',
          formTitle: 'Create account',
          submitLabel: 'CREATE ACCOUNT',
          alternateLabel: 'Already have an account? Sign in',
          alternateUrl: '/sign-in',
          images: [img(flexOfficeImages.SERVICE_DESK)],
          imageAlt: 'FlexOffice workspace onboarding',
          fields: [
            { label: 'First Name', type: 'text', required: true, visible: true },
            { label: 'Last Name', type: 'text', required: true, visible: true },
            { label: 'Email', type: 'email', required: true, visible: true },
            { label: 'Phone Number', type: 'tel', required: false, visible: true },
            { label: 'Password', type: 'password', required: true, visible: true },
          ],
        },
      },
    },
  },
};

/** Section presets list (transcribed from `flexOfficeSectionPresets`). */
export const flexOfficeSectionPresets = [
  { type: 'customHtml', title: 'Custom section', description: 'Start blank with sanitized HTML and scoped CSS.', highlight: true },
  { type: 'flexOfficeHero', title: 'Hero', description: 'Large image hero with booking CTA.' },
  { type: 'flexOfficeAbout', title: 'About', description: 'Image, story, quote, and CTA.' },
  { type: 'flexOfficeTimeline', title: 'Booking steps', description: 'Four-step booking process cards.' },
  { type: 'flexOfficeWorkingHours', title: 'Hours', description: 'Business hours and working days.' },
  { type: 'flexOfficeFeatures', title: 'Features', description: 'Feature cards with images.' },
  { type: 'flexOfficeCta', title: 'CTA', description: 'Signup/signin call to action.' },
  { type: 'flexOfficeServicesPage', title: 'Services', description: 'Service listing page shell.' },
  { type: 'flexOfficeServiceDetail', title: 'Detail', description: 'Workspace detail shell.' },
  { type: 'flexOfficeBookingSearch', title: 'Booking', description: 'Search and booking form shell.' },
  { type: 'flexOfficeBookings', title: 'Bookings', description: 'My bookings shell.' },
  { type: 'flexOfficeInvoice', title: 'Invoice', description: 'Invoice/payment shell.' },
  { type: 'flexOfficeContact', title: 'Contact', description: 'Contact form and support cards.' },
  { type: 'flexOfficeBookSpace', title: 'Book space', description: 'Booking search shell for /book-space.' },
  { type: 'flexOfficeSpaceTypes', title: 'Space types', description: 'Space-type browsing shell.' },
  { type: 'flexOfficeExploreLocations', title: 'Explore locations', description: 'Location browsing shell.' },
  { type: 'flexOfficeBookSchedule', title: 'Book schedule', description: 'Schedule selection booking shell.' },
  { type: 'flexOfficeChoice', title: 'Choose space', description: 'Space comparison/choice shell.' },
  { type: 'flexOfficeViewService', title: 'View service', description: 'Service schedule detail shell.' },
  { type: 'flexOfficeMarketingFeatures', title: 'Marketing features', description: 'Dark feature grid for /features.' },
  { type: 'flexOfficeConfirmBook', title: 'Confirm booking', description: 'Booking confirmation shell.' },
  { type: 'flexOfficeReviewSchedule', title: 'Review schedule', description: 'Schedule review shell.' },
  { type: 'flexOfficeOrders', title: 'My orders', description: 'Customer order/booking cards shell.' },
  { type: 'flexOfficeCustomerOrders', title: 'Customer orders', description: 'Customer order detail cards shell.' },
  { type: 'flexOfficeUserCustomerOrders', title: 'Customer orders (profile)', description: 'Profile customer-orders shell.' },
  { type: 'flexOfficeInvoiceDetail', title: 'Invoice detail', description: 'Invoice detail shell.' },
  { type: 'flexOfficeAboutPage', title: 'About FlexOffice', description: 'About page story and CTA shell.' },
  { type: 'flexOfficeAuth', title: 'Sign in / Sign up', description: 'Workspace account access form section.' },
];

export default FLEX_OFFICE_PRESETS;
